import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { AddMiniIcon } from '@calendly/ui/components/icons';
import { Loader } from '@calendly/ui/components/loader';
import { BodyText } from '@calendly/ui/components/typography';
import classNames from 'classnames';
import { useEffect, useMemo, forwardRef } from 'react';
import { Route, useHistory } from 'react-router-dom';
import { useEnforceUpgrade } from 'components/billing/useEnforceUpgrade';
import { selectTotal, useFetchCalendarAccountsQuery, useFetchMetadataQuery } from 'features/calendar_connections';
import { useIsSettingsModalLayout } from 'features/settings_modal/layout_context';
import { useLocalStorageAlerts } from 'hooks/use_local_storage_alerts';
import { ActiveCalendarConnections } from './active_calendar_connections';
import { ConnectCalendar } from './connect_calendar';
import { CALENDAR_CONNECTIONS_POPUP_ROUTE } from './constants';
import { CalendarAccountPopup } from './integration_provider';
import { PushCalendars } from './integration_provider/push_calendars';
const contentContainerCss = "c7xiig8";
const contentContainerSettingsModalFullWidthCss = "c1v12dei";
const calendarSettingsModalBodyConstrainCss = "catamkt";
const ContentContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(contentContainerCss, props.className)} />;
});
const accountCalendarsContainerCss = "a9tx0c3";
const AccountCalendarsContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(accountCalendarsContainerCss, props.className)} />;
});
const settingsModalCalendarHeaderTextsCss = "ssh1pxb";
const settingsModalCalendarHeaderRowCss = "ssb6slu";
const settingsModalCalendarHeaderButtonWrapCss = "sqo21pn";
const headerCss = "h1gg3jvh";
const Header = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(headerCss, props.className)} />;
});
const addCalendarAccountButtonCss = "arw2cao";
const AddCalendarAccountButton = forwardRef((props, ref) => {
  const decoration = 'secondary-outline';
  const responsive = false;
  const size = 'small';
  return <Button ref={ref} {...props} decoration={decoration} responsive={responsive} size={size} className={classNames(addCalendarAccountButtonCss, props.className)}>
      <AddMiniIcon />
      {' '}
      {I18n.t('publisher.availability_page.calendar_settings.connect_calendar_account')}
    </Button>;
});
export const CalendarSettings = () => {
  const history = useHistory();
  const isSettingsModalLayout = useIsSettingsModalLayout();
  const {
    data,
    isFetching,
    isSuccess
  } = useFetchCalendarAccountsQuery();
  const {
    data: {
      accountTotalLimit
    } = {},
    isLoading
  } = useFetchMetadataQuery();
  const connectionsCount = isSuccess && selectTotal(data);
  useLocalStorageAlerts();
  const nonConfiguredOutlookDesktopAccount = useMemo(() => !!data && Object.values(data.entities || {}).find(({
    kind,
    outlookDesktopConfigured
  }) => !outlookDesktopConfigured && kind === 'outlook_desktop'), [data]);
  const enforceUpgrade = useEnforceUpgrade('multiple_calendar_connections', {
    paywall: 'general_calendar_connections_add_calendar'
  });
  const handleAddCalendarClick = () => {
    enforceUpgrade(() => history.push(CALENDAR_CONNECTIONS_POPUP_ROUTE.newPath));
  };
  useEffect(() => {
    if (nonConfiguredOutlookDesktopAccount?.uuid) {
      history.push(`/app/availability/connected_calendars/outlook_desktop/${nonConfiguredOutlookDesktopAccount.uuid}/edit`);
    }
  }, [nonConfiguredOutlookDesktopAccount, history]);
  if (isFetching || isLoading) return <Loader size="small" />;
  return <ContentContainer role="region" className={classNames({
    [contentContainerSettingsModalFullWidthCss]: isSettingsModalLayout
  })}>
      <AccountCalendarsContainer>
        {!!connectionsCount && isSettingsModalLayout ? <div className={settingsModalCalendarHeaderRowCss}>
            <div className={settingsModalCalendarHeaderTextsCss}>
              <BodyText weight="bold">
                {I18n.t('publisher.availability_page.calendar_settings.check_conflicts.title')}
              </BodyText>
              <BodyText>
                {I18n.t('publisher.availability_page.calendar_settings.check_conflicts.description')}
              </BodyText>
            </div>
            {connectionsCount < accountTotalLimit ? <div className={settingsModalCalendarHeaderButtonWrapCss}>
                <AddCalendarAccountButton onClick={handleAddCalendarClick} />
              </div> : null}
          </div> : null}
        {!!connectionsCount && !isSettingsModalLayout ? <Header>
            <BodyText weight="bold">
              {I18n.t('publisher.availability_page.calendar_settings.check_conflicts.title')}
            </BodyText>
            {connectionsCount < accountTotalLimit ? <AddCalendarAccountButton onClick={handleAddCalendarClick} /> : null}
          </Header> : null}
        {isSettingsModalLayout ? <div className={calendarSettingsModalBodyConstrainCss}>
            {!connectionsCount ? <BodyText>
                {I18n.t('publisher.availability_page.calendar_settings.check_conflicts.description')}
              </BodyText> : null}
            {connectionsCount ? <ActiveCalendarConnections /> : <ConnectCalendar />}
          </div> : <>
            <BodyText>
              {I18n.t('publisher.availability_page.calendar_settings.check_conflicts.description')}
            </BodyText>
            {connectionsCount ? <ActiveCalendarConnections /> : <ConnectCalendar />}
          </>}
      </AccountCalendarsContainer>
      {!!connectionsCount && (isSettingsModalLayout ? <div className={calendarSettingsModalBodyConstrainCss}>
          <PushCalendars />
        </div> : <PushCalendars />)}
      <Route path={CALENDAR_CONNECTIONS_POPUP_ROUTE.rootPath} render={() => <CalendarAccountPopup isSettingsModalLayout={isSettingsModalLayout} />} />
    </ContentContainer>;
};

require("./calendar_settings.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./calendar_settings.js");